export interface IAnalytics {
    // Search
    onZeroAutocompleteResults: (context: string, searchTerm: string) => void;
    onContextChanged: (oldContext: string, newContext: string) => void;
    onSortChanged: (oldSort: string, newSort: string) => void;
    onPageChanged: (oldPage: number, newPage: number) => void;
    onFacetToggle: (facetName: string, selected: boolean) => void;

    //Advanced Search
    onLinkClick: () => void;
    onAddAnotherBoolean: () => void;
    onAdvancedSearchClick: () => void;

    // Transcripts
    onChildActivitiesToggled: () => void;
    onCreditClaimClick: () => void;
    onCreditClaimCancelClick: () => void;
    onCreditClaimConfirmClick: () => void;
    onCertificateViewClick: () => void;
    onCertificateGenerateClick: () => void;
    onCertificatePrintClick: () => void;
    onCertificateDownloadClick: () => void;
    onCertificateBackClick: () => void;
    onMOCCreditClaimSubmitClick: () => void;
    onMOCCreditClaimSuccess: () => void;
    onMOCCreditClaimError: () => void;
    onMOCCreditClaimCancelClick: () => void;
    onAnnualTranscriptsPrintClick: () => void;
    onTranscriptsSearch: (term: string) => void;
    onTranscriptsFilterClick: (name: string, value: string) => void;
    onTranscriptsSortClick: (name: string) => void;
    onTranscriptsDateChange: (year: string) => void;
    onTranscriptsHasChildren: () => void;    
}

export enum AnalyticsCategories {
    Search = 'Search',
    AdvancedSearch = 'Advanced Search',
    Transcripts = 'Transcripts'
}

export class GoogleAnalytics implements IAnalytics {

    private _dataLayer: any[];

    constructor() {
        this._dataLayer = (window as any).dataLayer || [];
    }

    private pushEvent = (category: AnalyticsCategories, event: string, label: string, value: string) => {
        const tag = {
            event,
            Category: category,
            Label: label,
            Value: value
        };
        console.log(`Sending tag to data layer: ${JSON.stringify(tag)}`)
        this._dataLayer.push(tag);
    }

    public pushFilterableListInfo = (filterableListPageTitle: string, filterableListUserInfo: string, filterableListPdfUrl: string) => {
        const event = "FilterableListDownloadEvent";
        const tag = {
            event,
            filterableListPageTitle: filterableListPageTitle,
            filterableListUserInfo: filterableListUserInfo,
            filterableListPdfUrl: filterableListPdfUrl
        };
        console.log(`Sending tag to data layer: ${JSON.stringify(tag)}`)
        this._dataLayer.push(tag);
    }

    public pushLockedContentUserInfo = (pageUrl: string, lockedContentUniqueUserId: string, userVisitorGroupName: string) => {
        const event = "LockedContentPageVisit";
        const tag = {
            event,
            lockedContentPageUrl: pageUrl,
            lockedContentUniqueUserId: lockedContentUniqueUserId,
            userVisitorGroupName: userVisitorGroupName
        };
        console.log(`Sending tag to data layer: ${JSON.stringify(tag)}`)
        this._dataLayer.push(tag);
    }

    public pushCampaignAndToolkitsInformation = (toolkitTitle: string, toolkitLanguage: string, toolkitUrl: string) => {
        const event = "CampaignAndToolkitsDownloadEvent";
        const tag = {
            event,
            toolkitTitle: toolkitTitle,
            toolkitLanguage: toolkitLanguage,
            toolkitUrl: toolkitUrl
        };

        console.log(`Sending tag to data layer: ${JSON.stringify(tag)}`)
        this._dataLayer.push(tag);
    }

    onZeroAutocompleteResults = (context: string, searchTerm: string) => {
        this.pushEvent(AnalyticsCategories.Search, 'ZeroAutocompleteResults', context.toLowerCase(), searchTerm.toLowerCase())
    }

    onContextChanged = (oldContext: string, newContext: string) => {
        this.pushEvent(AnalyticsCategories.Search, 'ContextChangedEvent', newContext.toLowerCase(), oldContext.toLowerCase());
    }

    onSortChanged = (oldSort: string, newSort: string) => {
        this.pushEvent(AnalyticsCategories.Search, 'SortClickedEvent', newSort.toLowerCase(), oldSort.toLowerCase());
    }

    onPageChanged = (oldPage: number, newPage: number) => {
        this.pushEvent(AnalyticsCategories.Search, 'PageClickedEvent', newPage.toString(), oldPage.toString());
    }

    onFacetToggle = (facetName: string, selected: boolean) => {
        this.pushEvent(AnalyticsCategories.Search, 'FacetToggeledEvent', facetName, selected.toString());
    }

    onLinkClick = () => {
        this.pushEvent(AnalyticsCategories.AdvancedSearch, 'AdvancedSearchLinkClickedEvent', undefined, undefined);
    }

    onAddAnotherBoolean = () => {
        this.pushEvent(AnalyticsCategories.AdvancedSearch, 'AdvancedSearchBooleanAddedEvent', undefined, undefined);
    }

    onAdvancedSearchClick = () => {
        this.pushEvent(AnalyticsCategories.AdvancedSearch, 'AdvancedSearchClickedEvent', undefined, undefined);
    }

    onChildActivitiesToggled = () => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'ChildActivitiesToggledEvent', undefined, undefined);
    }

    onCreditClaimClick = () => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'CreditClaimClickEvent', undefined, undefined);
    }

    onCreditClaimCancelClick = () => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'CreditClaimCancelEvent', undefined, undefined);
    }

    onCreditClaimConfirmClick = () => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'CreditClaimConfirmEvent', undefined, undefined);
    }

    onMOCCreditClaimSubmitClick = () => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'MOCCreditClaimSubmitEvent', undefined, undefined);
    }

    onMOCCreditClaimCancelClick = () => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'MOCCreditClaimCancelEvent', undefined, undefined);
    }

    onMOCCreditClaimError = () => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'MOCCreditClaimErrorEvent', undefined, undefined);
    }

    onMOCCreditClaimSuccess = () => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'MOCCreditClaimSuccessEvent', undefined, undefined);
    }

    onCertificateViewClick = () => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'CertificateViewEvent', undefined, undefined);
    }

    onCertificateGenerateClick = () => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'CertificateGenerateEvent', undefined, undefined);
    }

    onCertificatePrintClick = () => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'CertificatePrintEvent', undefined, undefined);
    }

    onCertificateDownloadClick = () => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'CertificateDownloadEvent', undefined, undefined);
    }

    onCertificateBackClick = () => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'CertificateBackEvent', undefined, undefined);
    }

    onAnnualTranscriptsPrintClick = () => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'AnnualTranscriptsPrintEvent', undefined, undefined);
    }

    onTranscriptsSearch = (term: string) => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'TranscriptsSearchEvent', term, undefined);
    }

    onTranscriptsFilterClick = (name: string, value: string) => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'TranscriptsFilterEvent', name, value);
    }

    onTranscriptsSortClick = (name: string) => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'TranscriptsSortEvent', name, undefined);
    }

    onTranscriptsDateChange = (year: string) => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'TranscriptsDateChangeEvent', year, undefined);
    }

    onTranscriptsHasChildren = () => {
        this.pushEvent(AnalyticsCategories.Transcripts, 'TranscriptsHasSeenChildrenEvent', undefined, undefined);
    }
}